/**
 * Einfaches Dashboard - Nur Rental Objects ohne Filter
 * Responsive Design für Desktop und Mobile mit Material-UI
 */

import React, { useState, useEffect } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Paper,
  useTheme,
  useMediaQuery,
  styled,
  CardActionArea,
} from "@mui/material";
import {
  AccessTime as AccessTimeIcon,
  Euro as EuroIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Close as CloseIcon,
  PlayArrow as PlayArrowIcon,
  Receipt as ReceiptIcon,
  Schedule as ScheduleIcon,
  Block as BlockIcon,
  LockOpen as LockOpenIcon,
} from "@mui/icons-material";
import {
  RentalObject,
  Article,
  Rental,
  RentalItem,
} from "../../types/api-types";
import { apiClient, ensureTokenIsSet } from "../../services/apiClient";
import InvoiceModalComponent from "./InvoiceModal";
import LoadingSpinner from "../Common/LoadingSpinner";
import { CircularProgress } from "@mui/material";

// Erweiterte Typen für Dashboard
interface DashboardRentalObject extends RentalObject {
  currentDuration?: string;
}

const SimpleDashboard: React.FC = () => {
  const [rentalObjects, setRentalObjects] = useState<DashboardRentalObject[]>(
    []
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal States
  const [selectedObject, setSelectedObject] =
    useState<DashboardRentalObject | null>(null);
  const [showStartModal, setShowStartModal] = useState(false);
  const [showRentedModal, setShowRentedModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);

  // Data States
  const [availableArticles, setAvailableArticles] = useState<Article[]>([]);
  const [selectedArticleIds, setSelectedArticleIds] = useState<string[]>([]);
  const [selectedBundleArticleIds, setSelectedBundleArticleIds] = useState<
    string[]
  >([]);
  const [currentRental, setCurrentRental] = useState<Rental | null>(null);

  // Auto-refresh alle 30 Sekunden
  useEffect(() => {
    const interval = setInterval(() => {
      loadRentalObjects(true);
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    loadRentalObjects();
  }, []);

  const loadRentalObjects = async (silent = false) => {
    try {
      if (!silent) setLoading(true);

      ensureTokenIsSet();
      const objects = await apiClient.getRentalObjects({ isActive: true });
      const objectsArray = Array.isArray(objects) ? objects : [];

      // Für vermietete Objekte Live-Preise laden
      const objectsWithPrices = await Promise.all(
        objectsArray.map(async (object) => {
          if (!object.isAvailable && object.currentRental) {
            try {
              // Live-Preis für das vermietete Objekt laden
              const livePrice = await apiClient.getRentalLivePrice(
                object.currentRental.id
              );
              return {
                ...object,
                currentPrice: livePrice?.currentTotal || 0,
                currentDuration: `${
                  Math.round(
                    ((livePrice?.currentDurationMinutes || 0) / 60) * 10
                  ) / 10
                }h`,
              };
            } catch (error) {
              console.error(
                `Failed to load live price for rental ${object.currentRental.id}:`,
                error
              );
              return { ...object, currentPrice: 0 };
            }
          }
          return object;
        })
      );

      setRentalObjects(objectsWithPrices);
      setError(null);
    } catch (err: any) {
      console.error("Failed to load rental objects:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadArticles = async () => {
    try {
      const articles = await apiClient.getArticles({ isActive: true });
      setAvailableArticles(Array.isArray(articles) ? articles : []);
    } catch (error) {
      console.error("Failed to load articles:", error);
    }
  };

  // Handle Click auf Rental Object
  const handleObjectClick = async (object: RentalObject) => {
    setSelectedObject(object);

    if (object.isAvailable || object.internalBlocker?.active) {
      // Verfügbar: Zeige Bundle-Auswahl (falls vorhanden) oder zusätzliche Artikel
      if (object.bundleArticles && object.bundleArticles.length > 0) {
        // Bundle-Artikel vorhanden - zeige Bundle-Auswahl-Modal
        await loadArticles(); // Lade auch zusätzliche Artikel für "ohne Bundle" Option
        setShowStartModal(true);
      } else {
        // Keine Bundle-Artikel - lade zusätzliche Artikel
        await loadArticles();
        setShowStartModal(true);
      }
    } else {
      // Vermietet: Zeige Optionen
      await loadArticles();
      if (object.currentRental) {
        try {
          const rentalWithItems = await apiClient.getRental(
            object.currentRental.id
          );
          setSelectedObject({
            ...object,
            currentRental: rentalWithItems,
          });
        } catch (error) {
          console.error("Failed to load current rental items:", error);
        }
      }
      setShowRentedModal(true);
    }
  };

  // Vermietung starten
  const handleStartRental = async (
    mode: "normal" | "bundle" | "without-bundle" = "normal",
    selectedBundleId?: string
  ) => {
    if (!selectedObject) return;

    try {
      let result;
      let articlesToAdd: string[] = [];
      let bundleId: string | undefined = undefined;

      if (mode === "bundle" && selectedBundleId) {
        // Bundle-Modus: Bundle ID setzen, NICHT als Artikel hinzufügen
        bundleId = selectedBundleId;
        articlesToAdd = selectedArticleIds; // Nur normale Artikel hinzufügen
      } else if (mode === "normal") {
        // Normale Artikel + ausgewählte Bundle-Artikel (für Rückwärtskompatibilität)
        articlesToAdd = [...selectedArticleIds, ...selectedBundleArticleIds];
      } else if (mode === "without-bundle") {
        // Nur normale Artikel, keine Bundle-Artikel
        articlesToAdd = selectedArticleIds;
      }

      // Sende bundleId als separaten Parameter
      result = await apiClient.quickStartRental(
        selectedObject.id,
        articlesToAdd,
        bundleId
      );

      const success =
        result.success || (result.data && result.data.success) || result.id;

      if (success) {
        // Modal schließen und neu laden
        closeAllModals();
      }
    } catch (error: any) {
      window.alert(`Fehler: ${error.message}`);
    }
  };

  const handleBlockObject = async () => {
    if (!selectedObject) return;

    try {
      await apiClient.setRentalObjectInternalBlocker(
        selectedObject.id,
        "Manuell belegt",
        "manual_lift_occupancy"
      );
      closeAllModals();
    } catch (error: any) {
      window.alert(`Fehler: ${error.message}`);
    }
  };

  const handleClearInternalBlocker = async () => {
    if (!selectedObject) return;

    try {
      await apiClient.clearRentalObjectInternalBlocker(selectedObject.id);
      closeAllModals();
    } catch (error: any) {
      window.alert(`Fehler: ${error.message}`);
    }
  };

  // Artikel zu laufender Vermietung hinzufügen
  const handleAddArticles = async (
    articleItems?: Array<{
      articleId: string;
      quantity?: number;
      startedAt?: Date;
      endedAt?: Date;
    }>
  ) => {
    if (!selectedObject?.currentRental || selectedArticleIds.length === 0)
      return;

    try {
      const result = await apiClient.addArticlesToRental(
        selectedObject.currentRental.id,
        selectedArticleIds,
        articleItems
      );
      const success = result.success || (result.data && result.data.success);

      if (success) {
        // Keine Alert-Benachrichtigung mehr
        setShowRentedModal(false);
        setSelectedObject(null);
        setSelectedArticleIds([]);
        loadRentalObjects(true);
      }
    } catch (error: any) {
      window.alert(`Fehler: ${error.message}`);
    }
  };

  const handleFinishRentalArticle = async (item: RentalItem) => {
    if (!selectedObject?.currentRental) return;

    try {
      await apiClient.updateRentalItem(
        selectedObject.currentRental.id,
        item.id,
        {
          endedAt: new Date(),
        }
      );
      const updatedRental = await apiClient.getRental(
        selectedObject.currentRental.id
      );
      setSelectedObject((prev) =>
        prev
          ? {
              ...prev,
              currentRental: updatedRental,
            }
          : prev
      );
      loadRentalObjects(true);
    } catch (error: any) {
      window.alert(`Fehler beim Beenden des Artikels: ${error.message}`);
    }
  };

  // Vermietung beenden - Zeige nur Vorschau-Modal (beende noch NICHT!)
  const handleEndRental = async () => {
    if (!selectedObject?.currentRental) return;

    try {
      console.log(
        "� Zeige Beenden-Vorschau für Rental:",
        selectedObject.currentRental.id
      );

      // NICHT beenden, nur Modal zur Vorschau öffnen
      setCurrentRental(selectedObject.currentRental);
      setShowRentedModal(false);
      setShowInvoiceModal(true);
    } catch (error: any) {
      console.error("❌ Fehler beim Anzeigen der Vorschau:", error);
      window.alert(`Fehler: ${error.message}`);
      // Auch bei Fehler Modal schließen und neu laden
      setShowRentedModal(false);
      loadRentalObjects(true);
    }
  };

  const closeAllModals = () => {
    setShowStartModal(false);
    setShowRentedModal(false);
    setShowInvoiceModal(false);
    setSelectedObject(null);
    setCurrentRental(null);
    setSelectedArticleIds([]);
    setSelectedBundleArticleIds([]);
    // Nach dem Schließen einer Modal immer neu laden
    loadRentalObjects(true);
  };

  if (loading) {
    return <LoadingSpinner text="Lade Mietobjekte..." />;
  }

  if (error) {
    return (
      <Paper
        sx={{
          p: 4,
          textAlign: "center",
          maxWidth: 500,
          mx: "auto",
          mt: 4,
          background: "linear-gradient(135deg, #ffebee 0%, #fce4ec 100%)",
        }}
      >
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="h5"
            color="error"
            sx={{ mb: 1, fontWeight: 600 }}
          >
            ❌ Fehler beim Laden
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {error}
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<RefreshIcon />}
          onClick={() => loadRentalObjects()}
          size="large"
        >
          Erneut versuchen
        </Button>
      </Paper>
    );
  }

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        backgroundColor: "background.default",
        minHeight: "100vh",
      }}
    >
      {/* Rental Objects Grid - Kompakt für maximale Anzahl */}
      <Grid container spacing={{ xs: 1, md: 2 }}>
        {rentalObjects.map((object) => (
          <Grid item xs={6} sm={4} md={3} lg={2} xl={2} key={object.id}>
            <ObjectCard
              object={object}
              onClick={() => handleObjectClick(object)}
            />
          </Grid>
        ))}
      </Grid>

      {/* Start Rental Modal */}
      {showStartModal && selectedObject && (
        <StartRentalModal
          object={selectedObject}
          articles={availableArticles}
          selectedArticleIds={selectedArticleIds}
          selectedBundleArticleIds={selectedBundleArticleIds}
          onArticleToggle={(id) => {
            setSelectedArticleIds((prev) =>
              prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
            );
          }}
          onBundleArticleToggle={(id) => {
            setSelectedBundleArticleIds((prev) =>
              prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
            );
          }}
          onStart={handleStartRental}
          onBlock={handleBlockObject}
          onClearBlock={handleClearInternalBlocker}
          onClose={closeAllModals}
        />
      )}

      {/* Rented Object Modal */}
      {showRentedModal && selectedObject && (
        <RentedObjectModal
          object={selectedObject}
          articles={availableArticles}
          selectedArticleIds={selectedArticleIds}
          onArticleToggle={(id) => {
            setSelectedArticleIds((prev) =>
              prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
            );
          }}
          onAddArticles={handleAddArticles}
          onFinishRentalArticle={handleFinishRentalArticle}
          onEndRental={handleEndRental}
          onClose={closeAllModals}
        />
      )}

      {/* Invoice Modal */}
      {showInvoiceModal && currentRental && (
        <InvoiceModalComponent
          rental={currentRental}
          rentalObjectId={selectedObject?.id} // ObjectId vom selectedObject weitergeben
          onClose={() => {
            setShowInvoiceModal(false);
            setCurrentRental(null);
            setSelectedObject(null);
            loadRentalObjects(true); // Reload nach dem Schließen
          }}
          onUpdate={() => {
            setShowInvoiceModal(false);
            setCurrentRental(null);
            setSelectedObject(null);
            loadRentalObjects(true); // Reload nach Update
          }}
        />
      )}
    </Box>
  );
};

// Styled Components - Kompakte Karten
const StyledCard = styled(Card)(({ theme }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  transition: "all 0.2s ease",
  cursor: "pointer",
  minHeight: "120px", // Feste Mindesthöhe für Konsistenz
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
  },
  "&.available": {
    border: "2px solid #4caf50",
    "& .MuiCardContent-root": {
      background: "linear-gradient(135deg, #e8f5e8 0%, #f1f8e9 100%)",
    },
  },
  "&.rented": {
    border: "2px solid #ff9800",
    "& .MuiCardContent-root": {
      background: "linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)",
    },
  },
  "&.blocked": {
    border: "2px solid #d32f2f",
    "& .MuiCardContent-root": {
      background:
        "repeating-linear-gradient(135deg, #ffebee 0, #ffebee 10px, #ffcdd2 10px, #ffcdd2 20px)",
    },
  },
  // Mobile Optimierung
  [theme.breakpoints.down("sm")]: {
    minHeight: "100px",
    "&:hover": {
      transform: "none", // Keine Hover-Effekte auf Mobile
    },
  },
}));

// Einzelne Object Card - Minimal Design
const ObjectCard: React.FC<{
  object: DashboardRentalObject;
  onClick: () => void;
}> = ({ object, onClick }) => {
  return (
    <StyledCard
      className={
        object.internalBlocker?.active
          ? "blocked"
          : object.isAvailable
          ? "available"
          : "rented"
      }
      onClick={onClick}
    >
      <CardActionArea sx={{ flexGrow: 1 }}>
        <CardContent
          sx={{
            p: 2, // Reduziertes Padding
            height: "100%",
            display: "flex",
            flexDirection: "column",
            minHeight: "120px", // Feste minimale Höhe
          }}
        >
          {/* Name und Status - kompakt */}
          <Box sx={{ mb: 1 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                mb: 0.5,
                fontSize: "1.1rem",
                lineHeight: 1.2,
              }}
            >
              {object.name}
            </Typography>
            <Chip
              label={
                object.internalBlocker?.active
                  ? "Intern belegt"
                  : object.isAvailable
                  ? "Verfügbar"
                  : "Vermietet"
              }
              size="small"
              color={
                object.internalBlocker?.active
                  ? "error"
                  : object.isAvailable
                  ? "success"
                  : "warning"
              }
              sx={{ fontWeight: 600, fontSize: "0.75rem" }}
            />
          </Box>

          {/* Preis nur wenn vermietet */}
          {!object.isAvailable && object.currentPrice && (
            <Box sx={{ mt: "auto" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <EuroIcon fontSize="small" color="primary" />
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 700,
                    color: "primary.main",
                    fontSize: "1.1rem",
                  }}
                >
                  {object.currentPrice.toFixed(2)}€
                </Typography>
              </Box>
              {object.currentDuration && (
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    fontSize: "0.8rem",
                    mt: 0.5,
                  }}
                >
                  {object.currentDuration}
                </Typography>
              )}
            </Box>
          )}
        </CardContent>
      </CardActionArea>
    </StyledCard>
  );
};

// Start Rental Modal
const StartRentalModal: React.FC<{
  object: DashboardRentalObject;
  articles: Article[];
  selectedArticleIds: string[];
  selectedBundleArticleIds: string[];
  onArticleToggle: (id: string) => void;
  onBundleArticleToggle: (id: string) => void;
  onStart: (
    mode: "normal" | "bundle" | "without-bundle",
    bundleId?: string
  ) => void;
  onBlock: () => Promise<void>;
  onClearBlock: () => Promise<void>;
  onClose: () => void;
}> = ({
  object,
  articles,
  selectedArticleIds,
  selectedBundleArticleIds,
  onArticleToggle,
  onBundleArticleToggle,
  onStart,
  onBlock,
  onClearBlock,
  onClose,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const hasBundleArticles =
    object.bundleArticles && object.bundleArticles.length > 0;
  const [isStarting, setIsStarting] = useState(false);
  const [isBlocking, setIsBlocking] = useState(false);
  const [isClearingBlock, setIsClearingBlock] = useState(false);
  const isBusy = isStarting || isBlocking || isClearingBlock;

  const handleStartWithLoading = async (
    mode: "normal" | "bundle" | "without-bundle",
    bundleId?: string
  ) => {
    setIsStarting(true);
    try {
      await onStart(mode, bundleId);
    } finally {
      setIsStarting(false);
    }
  };

  const handleBlockWithLoading = async () => {
    setIsBlocking(true);
    try {
      await onBlock();
    } finally {
      setIsBlocking(false);
    }
  };

  const handleClearBlockWithLoading = async () => {
    setIsClearingBlock(true);
    try {
      await onClearBlock();
    } finally {
      setIsClearingBlock(false);
    }
  };

  // Wenn Bundles vorhanden sind, zeige Bundle-Auswahl
  if (hasBundleArticles) {
    return (
      <Dialog
        open={true}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        fullScreen={fullScreen}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PlayArrowIcon />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Vermietung starten: {object.name}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{ color: "white" }}
            disabled={isBusy}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 3, color: "text.primary" }}>
            Wählen Sie eine Option:
          </Typography>

          <Grid container spacing={3}>
            {/* Bundle-Karten */}
            {object.bundleArticles!.map((bundleArticle) => (
              <Grid item xs={12} sm={6} key={bundleArticle.id}>
                <Card
                  sx={{
                    cursor: isBusy ? "not-allowed" : "pointer",
                    transition: "all 0.3s ease",
                    border: "2px solid transparent",
                    opacity: isBusy ? 0.7 : 1,
                    "&:hover": {
                      border: isBusy
                        ? "2px solid transparent"
                        : "2px solid #667eea",
                      transform: isBusy ? "none" : "translateY(-2px)",
                      boxShadow: isBusy
                        ? "none"
                        : "0 8px 20px rgba(0,0,0,0.15)",
                    },
                  }}
                  onClick={
                    isBusy
                      ? undefined
                      : () => handleStartWithLoading("bundle", bundleArticle.id)
                  }
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: "50%",
                          background:
                            "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mr: 2,
                        }}
                      >
                        <Typography sx={{ fontSize: "1.5rem" }}>📦</Typography>
                      </Box>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 600, mb: 0.5 }}
                        >
                          {object.name} +{" "}
                          {bundleArticle.article?.name ||
                            `Bundle ${bundleArticle.articleId}`}
                          {bundleArticle.quantity > 1 &&
                            ` (${bundleArticle.quantity}x)`}
                        </Typography>
                        <Chip
                          label="Bundle-Angebot"
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </Box>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="h5"
                        color="primary"
                        sx={{ fontWeight: 700 }}
                      >
                        {bundleArticle.bundlePrice.toFixed(2)} €
                        <Typography
                          component="span"
                          variant="body2"
                          sx={{ ml: 1, color: "text.secondary" }}
                        >
                          {object.rentalUnit === "MINUTES"
                            ? "pro Minute"
                            : object.rentalUnit === "HOURS"
                            ? "pro Stunde"
                            : "pro Tag"}
                        </Typography>
                      </Typography>
                    </Box>

                    {bundleArticle.notes && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        {bundleArticle.notes}
                      </Typography>
                    )}

                    <Button
                      variant="contained"
                      fullWidth
                      disabled={isBusy}
                      startIcon={
                        isStarting ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : undefined
                      }
                      sx={{
                        background:
                          "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        "&:hover": {
                          background:
                            "linear-gradient(135deg, #5a6fe8 0%, #6b459a 100%)",
                        },
                      }}
                    >
                      {isStarting ? "Starte Vermietung..." : "Auswählen"}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}

            {/* Ohne Bundle Option */}
            <Grid item xs={12} sm={6}>
              <Card
                sx={{
                  cursor: isBusy ? "not-allowed" : "pointer",
                  transition: "all 0.3s ease",
                  border: "2px solid transparent",
                  opacity: isBusy ? 0.7 : 1,
                  "&:hover": {
                    border: isBusy
                      ? "2px solid transparent"
                      : "2px solid #ff9800",
                    transform: isBusy ? "none" : "translateY(-2px)",
                    boxShadow: isBusy ? "none" : "0 8px 20px rgba(0,0,0,0.15)",
                  },
                }}
                onClick={
                  isBusy
                    ? undefined
                    : () => handleStartWithLoading("without-bundle")
                }
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        background:
                          "linear-gradient(135deg, #ff9800 0%, #f57c00 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mr: 2,
                      }}
                    >
                      <Typography sx={{ fontSize: "1.5rem" }}>⚡</Typography>
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, mb: 0.5 }}
                      >
                        Nur {object.name}
                      </Typography>
                      <Chip
                        label="Standard"
                        size="small"
                        color="warning"
                        variant="outlined"
                      />
                    </Box>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="h5"
                      color="warning.main"
                      sx={{ fontWeight: 700 }}
                    >
                      {object.basePrice?.toFixed(2) || "0.00"} €
                      <Typography
                        component="span"
                        variant="body2"
                        sx={{ ml: 1, color: "text.secondary" }}
                      >
                        {object.rentalUnit === "MINUTES"
                          ? "pro Minute"
                          : object.rentalUnit === "HOURS"
                          ? "pro Stunde"
                          : "pro Tag"}
                      </Typography>
                    </Typography>
                  </Box>

                  <Button
                    variant="contained"
                    color="warning"
                    fullWidth
                    disabled={isBusy}
                  >
                    {isStarting ? (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <CircularProgress size={16} color="inherit" />
                        Starte Vermietung...
                      </Box>
                    ) : (
                      "Auswählen"
                    )}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Loading Overlay */}
          {isBusy && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(255, 255, 255, 0.8)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1000,
                borderRadius: 1,
              }}
            >
              <Box sx={{ textAlign: "center" }}>
                <CircularProgress size={40} sx={{ mb: 2 }} />
                <Typography
                  variant="h6"
                  sx={{ color: "primary.main", fontWeight: 600 }}
                >
                  {isBlocking
                    ? "Objekt wird belegt..."
                    : isClearingBlock
                    ? "Interne Belegung wird aufgehoben..."
                    : "Vermietung wird gestartet..."}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Bitte warten Sie einen Moment
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            p: 3,
            gap: 2,
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "flex-end",
          }}
        >
          <Button
            onClick={onClose}
            variant="outlined"
            size="large"
            fullWidth={fullScreen}
            disabled={isBusy}
          >
            Abbrechen
          </Button>
          {!object.internalBlocker?.active && (
            <Button
              onClick={handleBlockWithLoading}
              variant="contained"
              color="error"
              size="large"
              fullWidth={fullScreen}
              disabled={isBusy}
              startIcon={
                isBlocking ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <BlockIcon />
                )
              }
            >
              {isBlocking ? "Belege..." : "Belegen"}
            </Button>
          )}
          {object.internalBlocker?.active && (
            <Button
              onClick={handleClearBlockWithLoading}
              variant="contained"
              color="success"
              size="large"
              fullWidth={fullScreen}
              disabled={isBusy}
              startIcon={
                isClearingBlock ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <LockOpenIcon />
                )
              }
            >
              {isClearingBlock ? "Gebe frei..." : "Freigeben"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    );
  }

  // Fallback: Alte Ansicht für Objekte ohne Bundles
  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <PlayArrowIcon />
          <Typography variant="h6">
            Vermietung starten: {object.name}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: "white" }} disabled={isBusy}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {articles.length > 0 ? (
          <>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Zusätzliche Artikel (optional):
            </Typography>
            <List
              sx={{
                maxHeight: {
                  xs: "calc(100vh - 400px)", // Mobile: genug Platz für Header/Buttons
                  sm: "calc(100vh - 350px)", // Tablet
                  md: "min(400px, calc(100vh - 300px))", // Desktop: max 400px oder verfügbare Höhe
                },
                overflow: "auto",
                minHeight: {
                  xs: 150, // Mindesthöhe für mobile Geräte
                  md: 200, // Etwas mehr für Desktop
                },
              }}
            >
              {articles.map((article) => (
                <ListItem key={article.id} disablePadding>
                  <ListItemButton
                    onClick={() => onArticleToggle(article.id.toString())}
                    disabled={isBusy}
                  >
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={selectedArticleIds.includes(
                          article.id.toString()
                        )}
                        tabIndex={-1}
                        disableRipple
                        disabled={isBusy}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={article.name}
                      secondary={`${article.price}€`}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </>
        ) : (
          <Typography variant="body1" color="text.secondary">
            Keine zusätzlichen Artikel verfügbar.
          </Typography>
        )}

        {/* Loading Overlay */}
        {isBusy && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
              borderRadius: 1,
            }}
          >
            <Box sx={{ textAlign: "center" }}>
              <CircularProgress size={40} sx={{ mb: 2 }} />
              <Typography
                variant="h6"
                sx={{ color: "primary.main", fontWeight: 600 }}
              >
                {isBlocking
                  ? "Objekt wird belegt..."
                  : isClearingBlock
                  ? "Interne Belegung wird aufgehoben..."
                  : "Vermietung wird gestartet..."}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Bitte warten Sie einen Moment
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          gap: 2,
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "flex-end",
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          fullWidth={fullScreen}
          disabled={isBusy}
        >
          Abbrechen
        </Button>
        {!object.internalBlocker?.active && (
          <Button
            onClick={handleBlockWithLoading}
            variant="contained"
            color="error"
            fullWidth={fullScreen}
            disabled={isBusy}
            startIcon={
              isBlocking ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <BlockIcon />
              )
            }
          >
            {isBlocking ? "Belege..." : "Belegen"}
          </Button>
        )}
        {object.internalBlocker?.active && (
          <Button
            onClick={handleClearBlockWithLoading}
            variant="contained"
            color="success"
            fullWidth={fullScreen}
            disabled={isBusy}
            startIcon={
              isClearingBlock ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <LockOpenIcon />
              )
            }
          >
            {isClearingBlock ? "Gebe frei..." : "Freigeben"}
          </Button>
        )}
        <Button
          onClick={() => handleStartWithLoading("without-bundle")}
          variant="contained"
          sx={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            "&:hover": {
              background: "linear-gradient(135deg, #5a6fe8 0%, #6b459a 100%)",
            },
          }}
          fullWidth={fullScreen}
          disabled={isBusy}
          startIcon={
            isStarting ? (
              <CircularProgress size={20} color="inherit" />
            ) : undefined
          }
        >
          {isStarting ? "Starte Vermietung..." : "Starten"}{" "}
          {selectedArticleIds.length > 0 &&
            `(${selectedArticleIds.length} Artikel)`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Rented Object Modal
const RentedObjectModal: React.FC<{
  object: DashboardRentalObject;
  articles: Article[];
  selectedArticleIds: string[];
  onArticleToggle: (id: string) => void;
  onAddArticles: (
    articleItems?: Array<{
      articleId: string;
      quantity?: number;
      startedAt?: Date;
      endedAt?: Date;
    }>
  ) => void;
  onFinishRentalArticle: (item: RentalItem) => void;
  onEndRental: () => void;
  onClose: () => void;
}> = ({
  object,
  articles,
  selectedArticleIds,
  onArticleToggle,
  onAddArticles,
  onFinishRentalArticle,
  onEndRental,
  onClose,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [isAddingArticles, setIsAddingArticles] = useState(false);
  const [isEndingRental, setIsEndingRental] = useState(false);
  const isHourlyArticle = (article: Article) =>
    (article.unit || "").trim().toLowerCase() === "stunde";
  const getBlockingExistingItem = (article: Article) => {
    const articleId = article.id.toString();
    const existingItems =
      object.currentRental?.items?.filter(
        (item) => item.articleId === articleId
      ) || [];

    if (isHourlyArticle(article)) {
      return existingItems.find((item) => !item.endedAt);
    }

    return undefined;
  };

  const getExistingArticleCount = (articleId: string) =>
    object.currentRental?.items?.filter((item) => item.articleId === articleId)
      .length || 0;

  const handleAddArticlesWithLoading = async () => {
    setIsAddingArticles(true);
    try {
      const articleItems = selectedArticleIds.map((articleId) => {
        const article = articles.find(
          (item) => item.id.toString() === articleId
        );

        return {
          articleId,
          quantity: 1,
          ...(article && isHourlyArticle(article)
            ? {
                startedAt: new Date(),
              }
            : {}),
        };
      });
      await onAddArticles(articleItems);
    } finally {
      setIsAddingArticles(false);
    }
  };

  const handleEndRentalWithLoading = async () => {
    setIsEndingRental(true);
    try {
      await onEndRental();
    } finally {
      setIsEndingRental(false);
    }
  };

  const formatDate = (date: string | Date) => {
    const d = new Date(date);
    return (
      d.toLocaleDateString("de-DE") +
      " " +
      d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })
    );
  };

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
      sx={{ maxHeight: "98vh", mt: 1 }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "linear-gradient(135deg, #f44336 0%, #d32f2f 100%)",
          color: "white",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ScheduleIcon />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {object.name} - Vermietet
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{ color: "white" }}
          disabled={isAddingArticles || isEndingRental}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2 }}>
        {/* Rental Info */}
        {object.currentRental && (
          <Paper
            sx={{
              p: 2,
              mt: 2,
              mb: 3,
              bgcolor: "warning.50",
              border: "1px solid",
              borderColor: "warning.200",
            }}
          >
            <Typography variant="h6" sx={{ mb: 1, color: "warning.dark" }}>
              Aktuelle Vermietung
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <AccessTimeIcon fontSize="small" />
              <Typography variant="body2">
                Seit: {formatDate(object.currentRental.startDate)}
              </Typography>
            </Box>
            {object.currentDuration && (
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <ScheduleIcon fontSize="small" color="info" />
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "info.main" }}
                >
                  Dauer: {object.currentDuration}
                </Typography>
              </Box>
            )}
            {object.currentPrice !== undefined && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <EuroIcon fontSize="small" />
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "success.main" }}
                >
                  Aktueller Gesamtpreis: {object.currentPrice.toFixed(2)}€
                </Typography>
              </Box>
            )}
          </Paper>
        )}

        <Paper
          sx={{
            border: "1px solid",
            borderColor: "divider",
            mb: 3,
          }}
        >
          <List
            sx={{
              maxHeight: {
                xs: "calc(100vh - 500px)", // Mobile: noch mehr Abzug für größere Rental Info Box
                sm: "calc(100vh - 450px)", // Tablet: noch mehr Abzug
                md: "min(300px, calc(100vh - 400px))", // Desktop: deutlich reduziert für größere Rental Info Box
              },
              overflow: "auto",
              minHeight: {
                xs: 120, // Noch kleinere Mindesthöhe wegen weniger Platz
                md: 150, // Reduzierte Mindesthöhe für Desktop
              },
            }}
          >
            {articles.map((article) => {
              const articleId = article.id.toString();
              const existingItem = getBlockingExistingItem(article);
              const existingCount = getExistingArticleCount(articleId);
              const isAlreadyRented = existingCount > 0;
              const isBlockedByRunningHourly = !!existingItem;
              const isRunningHourly =
                existingItem &&
                isHourlyArticle(article) &&
                !existingItem.endedAt;
              const selected = selectedArticleIds.includes(articleId);
              const hourly = isHourlyArticle(article);

              return (
                <ListItem key={article.id} disablePadding>
                  <ListItemButton
                    onClick={() => {
                      if (isRunningHourly && existingItem) {
                        onFinishRentalArticle(existingItem);
                        return;
                      }
                      onArticleToggle(articleId);
                    }}
                    disabled={
                      isAddingArticles ||
                      isEndingRental ||
                      (isBlockedByRunningHourly && !isRunningHourly)
                    }
                    sx={{
                      cursor: isRunningHourly ? "pointer" : undefined,
                    }}
                  >
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={selected || isBlockedByRunningHourly}
                        tabIndex={-1}
                        disableRipple
                        disabled={
                          isAddingArticles ||
                          isEndingRental ||
                          isBlockedByRunningHourly
                        }
                        sx={{
                          color: "#667eea",
                          "&.Mui-checked": {
                            color: "#667eea",
                          },
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {article.name}
                        </Typography>
                      }
                      secondary={
                        <Box>
                          <Typography
                            variant="body2"
                            color="#667eea"
                            sx={{ fontWeight: 600 }}
                          >
                            {article.price}€
                          </Typography>
                          {article.description && (
                            <Typography variant="body2" color="text.secondary">
                              {article.description}
                            </Typography>
                          )}
                          {hourly && selected && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: "block", mt: 0.5 }}
                            >
                              Start: jetzt. Der Artikel läuft, bis er im
                              Artikel-Tab beendet wird.
                            </Typography>
                          )}
                          {isAlreadyRented && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mt: 1,
                                flexWrap: "wrap",
                              }}
                            >
                              <Chip
                                size="small"
                                color={isRunningHourly ? "warning" : "success"}
                                label={
                                  isRunningHourly
                                    ? "Bereits hinzugefügt - Vermietung beenden"
                                    : `${existingCount}x bereits hinzugefügt`
                                }
                                clickable={!!isRunningHourly}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  if (isRunningHourly && existingItem) {
                                    onFinishRentalArticle(existingItem);
                                  }
                                }}
                              />
                            </Box>
                          )}
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Paper>

        {/* Loading Overlay */}
        {(isAddingArticles || isEndingRental) && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
              borderRadius: 1,
            }}
          >
            <Box sx={{ textAlign: "center" }}>
              <CircularProgress size={40} sx={{ mb: 2 }} />
              <Typography
                variant="h6"
                sx={{ color: "#667eea", fontWeight: 600 }}
              >
                {isAddingArticles
                  ? "Artikel werden hinzugefügt..."
                  : "Vermietung wird beendet..."}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Bitte warten Sie einen Moment
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          gap: 2,
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "flex-end",
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          size="large"
          fullWidth={fullScreen}
          disabled={isAddingArticles || isEndingRental}
        >
          Abbrechen
        </Button>
        <Button
          onClick={handleAddArticlesWithLoading}
          disabled={
            selectedArticleIds.length === 0 ||
            isAddingArticles ||
            isEndingRental
          }
          variant="contained"
          size="large"
          startIcon={
            isAddingArticles ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <AddIcon />
            )
          }
          fullWidth={fullScreen}
          sx={{
            backgroundColor: "#667eea",
            "&:hover": {
              backgroundColor: "#5a6fe8",
            },
          }}
        >
          {isAddingArticles
            ? "Füge Artikel hinzu..."
            : `Artikel hinzufügen (${selectedArticleIds.length})`}
        </Button>
        <Button
          onClick={handleEndRentalWithLoading}
          disabled={isAddingArticles || isEndingRental}
          variant="contained"
          color="error"
          size="large"
          startIcon={
            isEndingRental ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <ReceiptIcon />
            )
          }
          fullWidth={fullScreen}
        >
          {isEndingRental ? "Beende Vermietung..." : "Beenden & Abrechnen"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SimpleDashboard;
